import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { DataScopeStrategy } from 'shared';
import http from '@/utils/request';

/**
 * RBAC 权限状态管理（Pinia Store）
 *
 * 登录成功后调用 fetchPermissions() 拉取当前用户的权限列表，
 * 供 v-permission 指令、路由守卫、SensitiveField 组件使用。
 *
 * @see requirements.md - Requirement 12.1, 12.6, 12.7
 */
export const usePermissionStore = defineStore('permission', () => {
  /** 当前用户拥有的权限 code 列表 */
  const permissions = ref<string[]>([]);
  /** 各资源的数据范围策略 */
  const dataScopes = ref<Record<string, DataScopeStrategy>>({});
  /** 是否超级管理员（跳过所有权限校验） */
  const isSuper = ref<boolean>(false);

  /**
   * 从后端拉取当前用户的解析后权限
   *
   * 调用时机：登录成功后、页面刷新恢复 token 后
   */
  async function fetchPermissions(): Promise<void> {
    try {
      const data = (await http.get('/auth/permissions')) as unknown as {
        permissions?: string[];
        dataScopes?: Record<string, DataScopeStrategy>;
        isSuper?: boolean;
      };
      permissions.value = data.permissions ?? [];
      dataScopes.value = data.dataScopes ?? {};
      isSuper.value = data.isSuper ?? false;
    } catch {
      // 接口失败时不影响登录流程，权限列表为空（降级为无权限）
      permissions.value = [];
      dataScopes.value = {};
      isSuper.value = false;
    }
  }

  /**
   * 判断当前用户是否拥有指定权限
   *
   * isSuper 为 true 时直接返回 true，否则检查 permissions 列表
   */
  function hasPermission(code: string): boolean {
    if (isSuper.value) return true;
    return permissions.value.includes(code);
  }

  /**
   * 判断当前用户是否拥有指定权限中的任意一个
   *
   * isSuper 为 true 时直接返回 true，否则 any 语义
   */
  function hasAnyPermission(codes: string[]): boolean {
    if (isSuper.value) return true;
    return codes.some((code) => permissions.value.includes(code));
  }

  /**
   * 清空权限状态（登出时调用）
   */
  function clearPermissions(): void {
    permissions.value = [];
    dataScopes.value = {};
    isSuper.value = false;
  }

  function setPermissions(codes: string[], superAdmin = false, scopes: Record<string, DataScopeStrategy> = {}): void {
    permissions.value = [...codes];
    isSuper.value = superAdmin;
    dataScopes.value = { ...scopes };
  }

  return {
    permissions,
    dataScopes,
    isSuper,
    fetchPermissions,
    hasPermission,
    hasAnyPermission,
    clearPermissions,
    setPermissions,
  };
});
